import { createReducer } from '@reduxjs/toolkit'
import { Group, Permission } from 'interfaces/api/groups.interfaces'
import { ValidationErrorsApi } from 'packages/api'
import { createBuilder } from 'packages/redux-toolkit'

import {
  createGroup,
  getAllGroups,
  getAllPermissions,
  getGroup,
  getGroupPermissions,
  setGroupValidationErrors,
  setSingleGroup,
  updateGroup,
  updateGroupPermissions,
} from './actions'

interface GroupsState {
  list: {
    loading: boolean
    items: Group[]
    count: number
  }
  single: {
    loading: boolean
    item: Group | null
  }
  validation: ValidationErrorsApi | null
  permissions: {
    loading: boolean
    loadingSave: boolean
    isSuccess: boolean
    count: number
    items: Permission[]
    groupPermissions: number[]
  }
}

const initialState: GroupsState = {
  list: {
    loading: false,
    items: [],
    count: 0,
  },
  single: {
    loading: false,
    item: null,
  },
  validation: null,
  permissions: {
    loading: false,
    loadingSave: false,
    isSuccess: false,
    count: 0,
    items: [],
    groupPermissions: [],
  },
}

export const groupsReducer = createReducer(initialState, (builder) => {
  createBuilder(builder)
    .addCase(getAllGroups.pending, (state) => {
      state.list.loading = true
    })
    .addCase(getAllGroups.rejected, (state) => {
      state.list.loading = false
    })
    .addCase(getAllGroups.fulfilled, (state, { payload }) => {
      state.list.loading = false
      state.list.items = payload.results
      state.list.count = payload.count
    })

    .addCase(getGroup.pending, (state) => {
      state.single.loading = true
    })
    .addCase(getGroup.rejected, (state) => {
      state.single.loading = false
      state.single.item = null
    })
    .addCase([getGroup.fulfilled, setSingleGroup], (state, { payload }) => {
      state.single.loading = false
      state.single.item = payload
    })

    .addCase(setGroupValidationErrors, (state, { payload }) => {
      state.validation = payload
    })

    .addCase(getAllPermissions.pending, (state) => {
      state.permissions.loading = true
      state.permissions.items = []
      state.permissions.count = 0
    })
    .addCase(getAllPermissions.rejected, (state) => {
      state.permissions.loading = false
    })
    .addCase(getAllPermissions.fulfilled, (state, { payload }) => {
      state.permissions.loading = false
      state.permissions.items = payload.results
      state.permissions.count = payload.count
    })

    .addCase(getGroupPermissions.pending, (state) => {
      state.permissions.loading = true
      state.permissions.groupPermissions = []
    })
    .addCase(getGroupPermissions.rejected, (state) => {
      state.permissions.loading = false
    })
    .addCase(getGroupPermissions.fulfilled, (state, { payload }) => {
      state.permissions.loading = false
      state.permissions.groupPermissions = payload
    })

    .addCase(updateGroupPermissions.pending, (state, { meta }) => {
      state.permissions.groupPermissions = state.permissions.groupPermissions.filter(
        (item) => !meta.arg.data.permissionsExcludeIds.includes(item),
      )
      state.permissions.groupPermissions.push(...meta.arg.data.permissionsIncludeIds)
      state.permissions.loadingSave = true
      state.permissions.isSuccess = false
    })
    .addCase(updateGroupPermissions.rejected, (state) => {
      state.permissions.loadingSave = false
      state.permissions.isSuccess = false
    })
    .addCase(updateGroupPermissions.fulfilled, (state) => {
      state.permissions.loadingSave = false
      state.permissions.isSuccess = true
    })

    .addCase([createGroup.pending, updateGroup.pending], (state) => {
      state.single.loading = true
    })
    .addCase([createGroup.rejected, updateGroup.rejected], (state) => {
      state.single.loading = false
    })
    .addCase([createGroup.fulfilled, updateGroup.fulfilled], (state) => {
      state.single.loading = false
    })
})
